<template>
  <form-wrapper>
    <validation-observer
      v-for="(account, idx) in stepFormData"
      v-slot="{valid}"
      :key="idx"
      slim
    >
      <form-group>

        <form-group-title v-if="idx > 0" class="group__title">
          {{ $t('application-form.step-4.another-account') }}
          <icu-icon icon-name="delete" class="group__delete-btn" @click="deleteAccount(idx)" />
        </form-group-title>

        <div class="data-warning">
          <icu-icon class="pc-warning-small"></icu-icon>
          <div v-html="$t('application-form.step-4.data-warning')" />
        </div>

        <validation-provider v-slot="{errors}" rules="required">
          <icu-select
            :errors="[...errors, ...getFieldErrors('currency', idx)]"
            :options="currencyChoices"
            :value="account.currency"
            multiple
            :label="$t('application-form.step-4.currency')"
            :placeholder="$t('application-form.step-4.currency-hint')"
            required
            @input="emitInput('currency', $event, idx)"
          />
        </validation-provider>

        <validation-provider v-slot="{errors}" :rules="ibanValidator(idx)">
          <text-input
            ref="firstInputs"
            :errors="[...errors, ...getFieldErrors('iban', idx)]"
            :value="account.iban"
            :label="$t('application-form.step-4.iban')"
            placeholder="UA213223130000026007233566001"
            :hint="$t('application-form.step-4.iban-hint')"
            :required="ibanValidator(idx).required"
            @input="emitInput('iban', $event, idx)"
          />
        </validation-provider>
        <template v-if="account.currency.includes(2) || account.currency.includes(3)">
          <validation-provider v-slot="{errors}">
            <text-input
              :errors="[...errors, ...getFieldErrors('swift', idx)]"
              :value="account.swift"
              :label="$t('application-form.step-4.swift')"
              @input="emitInput('swift', $event, idx)"
            />
          </validation-provider>

          <validation-provider v-slot="{errors}" rules="">
            <text-input
              :errors="[...errors, ...getFieldErrors('additional_information', idx)]"
              :value="account.additional_information"
              :label="$t('application-form.step-4.additional_information')"
              @input="emitInput('additional_information', $event, idx)"
            />
          </validation-provider>

          <validation-provider v-slot="{errors}" :rules="bankNameValidator(idx)">
            <text-input
              ref="bankNameInputs"
              :errors="[...errors, ...getFieldErrors('bank', idx)]"
              :value="account.bank"
              :label="$t('application-form.step-4.bank')"
              :placeholder="isResident? 'ПриватБанк': 'PrivatBank'"
              :hint="$t('application-form.step-4.bank-hint')"
              @input="emitInput('bank', $event, idx)"
            />
          </validation-provider>
        </template>

        <validation-provider v-slot="{errors, validate}" rules="files_has_no_errors">
          <icu-dropzone
            :errors="[...getFieldErrors('doc', idx), ...errors]"
            :value="account.doc"
            :hint="$t('application-form.step-4.doc-hint')"
            :label="$t('application-form.step-4.doc')"
            :is-resident="isResident"
            required
            :maximum-files="account.currency.length * 2 || 2"
            @input="emitFile('doc', $event, idx)"
            @delete-file="deleteFile('doc', $event, idx)"
            @files-uploaded="validate"
          />
        </validation-provider>
        <validation-observer
          v-if="stepFormData[idx].currency.includes(1)"
          v-slot="{valid, invalid, errors}"
          slim
        >
          <div
            v-for="(card,index) in stepFormData[idx].cards"
            :key="index"
            class="card-block-wrap"
          >
            <div :key="card.id" class="card-data">
              <icu-icon
                v-if="index > 0"
                icon-name="delete"
                class="group__delete-btn"
                @click="$emit('delete-card', idx, index)"
              />
              <validation-provider
                v-slot="{errors, invalid}"
                :rules="cardNumberValidator(idx, index, card.card_number)"
              >
                <text-input
                  :errors="[...errors, ...getFieldErrors('cards', idx, index, 'card_number')]"
                  :value="card.card_number"
                  :label="$t('application-form.step-4.card_number')"
                  :hint="$t('application-form.step-4.card_number-hint')"
                  :mask="/^[0-9a-zA-Z]*$/"
                  class="card-number"
                  :required="cardNumberValidator(idx, index, card.card_number).required"
                  @input="updateCard('card_number', $event, idx, index)"
                />
              </validation-provider>

              <validation-provider v-slot="{errors, invalid }" :rules="cardExpiredValidator(idx, index, card.card_number)">
                <text-input
                  :errors="[...errors, ...getFieldErrors('cards', idx, index, 'card_expire')]"
                  :value="card.card_expire"
                  :label="$t('application-form.step-4.card_expire')"
                  :hint="$t('application-form.step-4.card_expire-hint')"
                  placeholder="06/22"
                  mask="00{/}00"
                  :required="cardExpiredValidator(idx, index, card.card_number).required"
                  @input="updateCard('card_expire', $event, idx, index)"
                />
              </validation-provider>

              <div v-if="isResident" class="pc-bank-card__information">
                <icu-icon class="pc-warning-small"></icu-icon>
                <div v-html="$t('pc.accounts.e-support-text')" />
              </div>
              <br />
              <validation-provider v-slot="{errors, validate}" rules="files_has_no_errors">
                <icu-dropzone
                  :errors="[...errors, ...getFieldErrors('cards', idx, index, 'doc')]"
                  :value="card.doc"
                  :hint="$t('pc.accounts.card-confirm-image-hint')"
                  :label="$t('pc.accounts.card-confirm-image-label')"
                  :is-resident="isResident"
                  required
                  @input="emitFile(`cards[${index}].doc`, $event, idx)"
                  @delete-file="deleteFile(`cards[${index}].doc`, $event, idx)"
                  @files-uploaded="validate"
                />
              </validation-provider>
            </div>
          </div>
          <pc-button
            v-if="showAddCardButton(idx, valid)"
            icon-left-name="plus"
            variant="void"
            class="add-card"
            @click="addCard(idx)"
          >
            {{ $t('application-form.step-4.add-card-1', {more: more(idx)}) }} <br />{{ $t('application-form.step-4.add-card-2') }}
          </pc-button>
        </validation-observer>
      </form-group>
    </validation-observer>
  </form-wrapper>
</template>

<script>
import FormWrapper from '@/components/views/RegisterPage/FormWrapper.vue'
import FormGroup from '@/components/views/RegisterPage/FormGroup.vue'
import TextInput from '@/components/inputs/TextInput.vue'
import IcuSelect from '@/components/inputs/IcuSelect.vue'
import IcuDropzone from '@/components/inputs/IcuDropzone.vue'
import FormGroupTitle from '@/components/views/RegisterPage/FormGroupTitle.vue'
import IcuIcon from '@/components/common/IcuIcon.vue'
import {ValidationProvider, ValidationObserver} from 'vee-validate'
import PcButton from '@/components/common/PcButton.vue'
import { api } from '@/api/api.js'

export default {
  name: 'FourthStep',
  components: { PcButton, IcuIcon, ValidationProvider, ValidationObserver, FormGroupTitle, IcuDropzone, IcuSelect, TextInput, FormGroup, FormWrapper },
  props: {
    stepFormData: {
      type: Array,
      required: true
    },
    backendErrors: {
      type: [Object, Array],
      default: () => []
    },
    isValid:{
      type: Boolean,
      default: false
    },
    settings:{
      type: Object,
      default:()=> {}
    },
    isResident: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      currencyChoices: [],
    }
  },
  async mounted() {
    const { data } = await api.profile.getCurrencies()
    this.currencyChoices = data


    // this.stepFormData.forEach((account, idx) => {
    //   if(!account.cards.length) {
    //     this.addCard(idx)
    //   }
    // })

  },
  methods: {
    more(idx) {
      if( this.stepFormData[idx].cards.length) {
        return this.$i18n.locale === 'uk' ? ' ще' : ' another'
      }
      return ''
    },
    ibanValidator(idx){
      const data = {
        required:true,
        isIbanUniq:{accounts: this.getAllIban(idx)},
      }
      // if(!this.isResident) {
      //   data.required = false
      //   if (!this.$refs.bankNameInputs?.[idx].value) {
      //     data.min = 0
      //   }
      // }
      // if(this.isResident){
      //   data.min_no_required = 15
      //   data.max= 34
      //   data.iban = true
      // } else {
      //   data.neres_iban = true
      // }
      return data

    },
    bankNameValidator(idx){
      const data = {
        required: false
      }

      return data
    },
    cardNumberValidator(idx, index, value){
      const data = {
        required: false,
        isCardUniq: {cards: this.getAllCards(idx, index)}
      }
      data.min= value? 14 : false
      data.max= 19
      return data
    },
    cardExpiredValidator(idx, index, value){
      const cardValidator = this.cardNumberValidator(idx, index, value)
      const data = {
        required: cardValidator.required || !!value?.length,
        ua_card_expire: true
      }
      return data
    },
    checkAccountCardsValues(idx){
      return this.stepFormData[idx].cards.reduce((acc,card)=>{
        return acc && card.card_number && card.card_expire
      },true)

    },
    addCard(idx){
      this.$emit('add-card', idx)
    },
    getAllIban(idx) {
      const accounts =[]
      this.stepFormData.forEach((acc, index) => {
        if(index !== idx){
          accounts.push(acc.iban)
        }
      })
      return accounts
    },
    getAllCards(accountIndex, cardIndex) {
      const cards = []
      this.stepFormData.forEach((account, accIndex)=>{
         account.cards.forEach((card, index) =>{
           if(accIndex > accountIndex){
             return
           } else if( accIndex === accountIndex && index >= cardIndex){
             return
           }
           cards.push(card.card_number)
         })
      })
      return cards
    },
    emitInput(formKey, value, idx) {
      const arrayFormKey = `${idx}.${formKey}`
      this.$emit('update-field', {formKey: arrayFormKey, value})
    },
    emitFile(formKey, file, idx) {
      const arrayFormKey = `${idx}.${formKey}`
      this.$emit('add-file', {formKey: arrayFormKey, file})
    },
    deleteFile(formKey, file, idx) {
      const arrayFormKey = `${idx}.${formKey}`
      this.$emit('delete-file', {formKey: arrayFormKey, file})
    },
    deleteAccount(accountIdx) {
      this.$emit('delete-array-item', accountIdx)
    },
    getFieldErrors(fieldName, accountIndex, index, subfieldName) {
      if (index !== undefined){
        if(this.backendErrors[accountIndex]) {
          return this.backendErrors[accountIndex]?.[fieldName]?.[index]?.[subfieldName] ?? []
        } else {
          return []
        }
      } else {
        return this.backendErrors[accountIndex]?.[fieldName] ?? []
      }
    },
    async updateCard(key, value, idx, cardIndex){

      if(key === 'card_expire' && value === '/'){
        value = ''
      }
      this.emitInput(`cards.${cardIndex}.${key}`, value, idx)
    },
    showAddCardButton(idx, valid){
      return this.checkAccountCardsValues(idx)
          && valid
          && this.stepFormData[idx].cards.length < this.settings.MAX_IBAN_CARDS_COUNT
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/theme.scss";

.group {
  &__title {
    display: flex;
    align-items: center;
    position: relative;
  }

  &__delete-btn {
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}
.card-data {
  position: relative;
  .delete {
    right: 15px;
    top: 50px;
    z-index: 1;
  }
  .card-number {
    margin-bottom: 10px;
  }
}
.card-block-wrap {
  margin-bottom: 20px;
}

.pc-bank-card__information {
  display: flex;
  font-size: 14px;
  padding-bottom: 16px;
  margin-top: 10px;
  .pc-warning-small {
    margin-right: 10px;
  }
  * {
    color: $passive-text
  }
}

.data-warning {
  display: flex;
  font-size: 14px;
  color: $error-text;
  .pc-warning-small {
    margin-right: 10px;
  }
  * {
    color: $error-text;
  }
}
</style>
